import _ from 'lodash'
import mutationsHelpers from '@/store/modules/rev/mutationsHelpers.js'

/* Due to some technical debt in the backend side we have some weird values in the "cycle.reviewImplemented" field where:
- Not implemented rev = null
- Implemented rev = 2
*/
const IMPLEMENTED = 2
const getIsImplemented = rev => {
  const implementedStatus = rev?.cycle?.reviewImplemented ?? null
  return implementedStatus === IMPLEMENTED
}

function serializeImprovementIdeaForLocalState(rev, defaultRev = {}) {
  const exchangeRate = rev?.financialMetadata?.exchangeRateFromGlobal ?? 1
  const costReduction =
    rev?.cycle?.financialInformationCaptured?.reviewCostReductionPerMonth
      .value ?? null
  const investment =
    rev?.cycle?.financialInformationCaptured?.reviewTotalInvestmentMade
      ?.value ?? null
  const revenue =
    rev?.cycle?.financialInformationCaptured?.reviewNewRevenuePerMonth?.value ??
    null

  return Object.assign(defaultRev, {
    id: rev.id,
    _id: rev._id,
    author: rev.author,
    name: rev.name,
    phase: rev.revState,
    status: rev.revCondition,
    points: rev.points,
    team: {
      owner: rev.owner ? mutationsHelpers.formatOwner(rev.owner) : {},
      coach: rev.coach ? mutationsHelpers.formatCoach(rev.coach) : {},
      collaborators: rev.collaborators
        ? rev.collaborators.map(mutationsHelpers.formatUser)
        : [],
    },
    challenge: _.get(rev, 'challenge', {}),
    location: {
      organization: _.get(rev, 'organization', {}),
      site: _.get(rev, 'site', {}),
      tags: _.get(rev, 'tags', []),
    },
    categories: rev.categories || [],
    financialMetadata: {
      revCurrency: _.get(rev, 'financialMetadata.currency', ''),
      exchangeRate,
      organizationCurrency: _.get(rev, 'organization.currency', ''),
    },
    relate: {
      problem: _.get(rev, 'cycle.relateProblem') || '',
      idea: _.get(rev, 'cycle.relateIdea') || '',
      expectedResult: _.get(rev, 'cycle.relateResult') || '',
      media: rev?.cycle?.relatePhotos ?? [],
      categories: rev.categories || [],
    },
    experiment: {
      whatDidYouDo: _.get(rev.cycle, 'experimentDone') || '',
      media: rev?.cycle?.experimentPhotos ?? [],
    },
    verify: {
      didExperimentWork: _.get(rev.cycle, 'verify', null),
      learned: _.get(rev.cycle, 'expandLearn') || '',
    },
    expand: {
      media: rev?.cycle?.expandPhotos ?? [],
      solution: _.get(rev.cycle, 'expandSustain') || '',
      isImplemented: getIsImplemented(rev),
      whyNotImplemented:
        _.get(rev.cycle, 'reviewNotImplementedExplanation') || '',
    },
    review: {
      costReduction,
      costReductionExchange: _.isNumber(costReduction)
        ? Math.round(costReduction / exchangeRate)
        : null,
      investment,
      investmentExchange: _.isNumber(investment)
        ? Math.round(investment / exchangeRate)
        : null,
      revenue,
      revenueExchange: _.isNumber(revenue)
        ? Math.round(revenue / exchangeRate)
        : null,
      estimationComment: _.get(rev.cycle, 'reviewComments') || '',
      timeReductionByMonth: rev?.cycle?.reviewTimeReductionByMonth ?? null,
      timeInvested: rev?.cycle?.reviewTimeInvested ?? null,
    },
    validation: {
      implementation: _.get(rev, 'validations__implementation__isValidated'),
      financial: _.get(rev, 'validations__financial__isValidated'),
      highlight: _.get(rev, 'highlight__isHighlighted'),
      implementationWasValidated: _.get(
        rev,
        'validations__implementation__hasAnswer'
      ),
      financialWasValidated: _.get(rev, 'validations__financial__hasAnswer'),
      implementationValidatedBy: rev.validations__implementation__validatedBy
        ? mutationsHelpers.formatUser(
            rev.validations__implementation__validatedBy
          )
        : null,
      implementationValidatedAt: rev?.validations__implementation__validatedAt,
      financialValidatedAt: rev?.validations__financial__validatedAt,
      financialValidatedBy: rev.validations__financial__validatedBy
        ? mutationsHelpers.formatUser(rev.validations__financial__validatedBy)
        : null,
      implementationId: _.get(rev, 'validations__implementation__id'),
      financialValidationId: _.get(rev, 'validations__financial__id'),
    },
    validationRequest: rev?.validationRequest,
    validatedReview: rev?.validatedReview,
    notes: rev.notes,
    likes: rev.likes,
    updatedAt: rev.updatedAt,
    createdAt: rev.createdAt,
    completedAt: rev?.finishedAt,
    likedByMe: rev.likedByMe,
    approvedByMe: rev.approvedByMe,
    approvals: rev.approvals,
    isApproved: rev.isApproved,
    totalLikes: rev.totalLikes,
    priority: rev?.priority,
    dueDate: rev?.dueDate,
    lang: rev?.lang,
    onHoldReason: rev?.onHoldReason || null,
    onHoldBy: rev?.onHoldBy || null,
    onHoldAt: rev?.onHoldAt || null,
    canceledReason: rev?.canceledReason || null,
    canceledBy: rev?.canceledBy || null,
    canceledAt: rev?.canceledAt || null,
    reviewPhase: rev?.reviewPhase ?? {},
    impactEndsAt: rev?.impactEndsAt,
    impactStartsAt: rev?.impactStartsAt,
    implementedAt: rev?.implementedAt,
    replicatedFromRevId: rev?.replicatedFromRevId || null,
    routineId: rev?.routineId || null,
    findingId: rev?.findingId || null,
    predictedFinancialImpactInUSD: rev?.predictedFinancialImpactInUSD ?? null,
  })
}

const revSerializer = {
  serializeImprovementIdeaForLocalState,
}

export default revSerializer
